import { ArticlesList } from '@/components/orders/show/ArticlesList'
import { CustomerBlock } from '@/components/orders/show/CustomerBlock'
import { HandoverStep, HandoverTimeline } from '@/components/orders/show/HandoverTimeline'
import { OrderHeader } from '@/components/orders/show/OrderHeader'
import { StatusRow } from '@/components/orders/show/StatusRow'
import { useOrderActions } from '@/hooks/useOrderActions'
import { useRole } from '@/hooks/useRole'
import { Order, SharedProps } from '@/types'
import { latestVerifiedAt, latestVerifier } from '@/utils/format'
import { canDeleteOrder } from '@/utils/orders'
import { Head, router, usePage } from '@inertiajs/react'
import { Badge, Button, Card, Divider, Menu, Stack } from '@mantine/core'
import { IconCalendarUser, IconCash, IconCreditCard, IconMessage } from '@tabler/icons-react'
import dayjs from 'dayjs'

export default function AtelierOrderShow({ order }: { order: Order }) {
  const { current_organization } = usePage<SharedProps>().props
  const { isAdmin, isAccueil } = useRole()
  const { markCompleted, markDelivered, handleSendSms, handlePayment, handleDeleteOrder } = useOrderActions(
    order.id,
    current_organization?.slug || ''
  )
  if (!current_organization) return null

  const canDelete = canDeleteOrder(order, { isAdmin, isAccueil })

  const { id: orderId, sms_sent_at, paid_at, completed_at, planned_date } = order
  const isStoreOrder = order.contract?.customer_organization_id != null
  const isProOrder = order.contract != null
  const isPickupContract = order.collected_by_provider

  const productionDone = order.status === 'ready' || order.status === 'completed'

  const smsDescription = (() => {
    if (sms_sent_at) {
      return `Envoyé le ${dayjs(sms_sent_at).format('DD/MM/YYYY HH:mm')}${order.sms_sender ? ` (${order.sms_sender.name})` : ''}`
    }
    return productionDone ? 'Prêt à être envoyé' : 'En attente de production'
  })()

  const paymentDescription = paid_at
    ? `Payé le ${dayjs(paid_at).format('DD/MM/YYYY HH:mm')} (${[order.cashier?.name, order.payment_method === 'card' ? 'Carte' : 'Espèces'].filter(Boolean).join(', ')})`
    : 'En attente de paiement'

  const workDoneAt = latestVerifiedAt(order)

  const handoverSteps: HandoverStep[] = (() => {
    const steps: HandoverStep[] = []

    if (isPickupContract || order.collected_at) {
      steps.push({
        key: 'collected',
        label: 'Collectée chez la boutique',
        date: order.collected_at,
        actorName: order.collector?.name,
        done: Boolean(order.collected_at),
      })
    }

    steps.push({
      key: 'production',
      label: 'Travail terminé en atelier',
      date: workDoneAt,
      actorName: latestVerifier(order),
      done: productionDone,
    })

    if (isProOrder) {
      steps.push({
        key: 'delivered',
        label: isStoreOrder ? 'Livrée à la boutique' : 'Livrée à la société',
        date: order.delivered_at,
        actorName: order.deliverer?.name,
        done: Boolean(order.delivered_by_id),
        action: isStoreOrder ? undefined : (
          <Button size="xs" variant="light" onClick={markDelivered} disabled={!productionDone}>
            Marquer comme livrée
          </Button>
        ),
      })
      return steps
    }

    steps.push({
      key: 'handoff',
      label: 'Remise au client',
      date: completed_at,
      actorName: order.deliverer?.name,
      done: Boolean(completed_at),
      action: (
        <Button size="xs" variant="light" onClick={markCompleted} disabled={!productionDone}>
          Marquer comme remis
        </Button>
      ),
    })
    return steps
  })()

  return (
    <>
      <Head title={`Commande #${orderId}`} />

      <Card withBorder radius="md" padding="lg" shadow="sm">
        <Stack gap="lg">
          <OrderHeader
            order={order}
            organizationSlug={current_organization.slug}
            canEdit
            canDelete={canDelete}
            onDelete={handleDeleteOrder}
          />

          <CustomerBlock
            name={order.customer_name}
            phoneNumber={order.customer_phone}
            variant={isProOrder ? 'organization' : 'individual'}
          />

          <Divider />

          <Stack gap="sm">
            <StatusRow
              icon={<IconCalendarUser width="70%" height="70%" />}
              color="dark"
              label="Date prévue"
              description="pour la collecte client"
              action={
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => router.visit(`/organizations/${current_organization.slug}/orders/${orderId}/edit`)}
                >
                  {planned_date ? dayjs(planned_date).format('ddd, D MMM YYYY') : 'Date non définie'}
                </Button>
              }
            />

            {!isProOrder && (
              <StatusRow
                icon={<IconMessage width="70%" height="70%" />}
                label="SMS"
                description={smsDescription}
                action={
                  sms_sent_at ? (
                    <Badge color="green" variant="light">
                      SMS Envoyé
                    </Badge>
                  ) : (
                    <Button size="xs" variant="light" onClick={handleSendSms} disabled={!productionDone}>
                      Envoyer SMS
                    </Button>
                  )
                }
              />
            )}

            {!isProOrder && (
              <StatusRow
                icon={<IconCreditCard width="70%" height="70%" />}
                label="Paiement"
                description={paymentDescription}
                action={
                  paid_at ? (
                    <Badge color="green" variant="light">
                      Payé
                    </Badge>
                  ) : (
                    <PaymentMenus canPayAndCollect={productionDone} onPay={handlePayment} />
                  )
                }
              />
            )}
          </Stack>

          <HandoverTimeline title="Parcours" steps={handoverSteps} />

          <Divider />

          <ArticlesList order={order} organizationSlug={current_organization.slug} />
        </Stack>
      </Card>
    </>
  )
}

function PaymentMenus({
  canPayAndCollect,
  onPay,
}: {
  canPayAndCollect: boolean
  onPay: (method: 'cash' | 'card', collectAfter?: boolean) => void
}) {
  return (
    <>
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <Button size="xs" variant="light">
            Marquer comme payé
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconCreditCard size={14} />} onClick={() => onPay('card')}>
            Carte Bancaire
          </Menu.Item>
          <Menu.Item leftSection={<IconCash size={14} />} onClick={() => onPay('cash')}>
            Espèces
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Menu shadow="md" width={200}>
        <Menu.Target>
          <Button size="xs" variant="filled" disabled={!canPayAndCollect}>
            Marquer comme payé et remis
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconCreditCard size={14} />} onClick={() => onPay('card', true)}>
            Carte Bancaire
          </Menu.Item>
          <Menu.Item leftSection={<IconCash size={14} />} onClick={() => onPay('cash', true)}>
            Espèces
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  )
}
